// SPDX-License-Identifier: AGPL-3.0-or-later

import Emoji from '@app/features/emoji/state/Emoji';
import type {FlatEmoji} from '@app/features/emoji/types/EmojiTypes';
import * as GuildEmojiCommands from '@app/features/expressions/commands/GuildEmojiCommands';
import ExpressionMetadata from '@app/features/expressions/state/ExpressionMetadata';
import {GuildIcon} from '@app/features/guild/components/popouts/GuildIcon';
import Guilds from '@app/features/guild/state/Guilds';
import Permission from '@app/features/permissions/state/Permission';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {failureValidationErrors} from '@app/features/platform/utils/ResponseInspection';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import {MenuItemSubmenu} from '@app/features/ui/action_menu/MenuItemSubmenu';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {createKeyedActionGuard} from '@app/lib/overlay/keyedActionGuard';
import {Permissions} from '@app/vendor/constants/ChannelConstants';
import {useLingui} from '@lingui/react/macro';
import {CopyIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect} from 'react';

const logger = new Logger('CloneEmoji');

const emojiCloneGuard = createKeyedActionGuard();
async function blobToDataUrl(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(String(reader.result));
		reader.onerror = () => reject(new Error('Failed to read image'));
		reader.readAsDataURL(blob);
	});
}
interface CloneEmojiMenuItemProps {
	emoji: FlatEmoji;
	onClose: () => void;
}
export const CloneEmojiMenuItem: React.FC<CloneEmojiMenuItemProps> = observer(({emoji, onClose}) => {
	const {t} = useLingui();
	const sourceGuild = emoji.guildId ? Guilds.getGuild(emoji.guildId) : null;
	const metadataState = emoji.id ? ExpressionMetadata.getEmojiMetadata(emoji.id) : null;
	const needsMetadataLookup = Boolean(emoji.id) && !sourceGuild;
	useEffect(() => {
		if (
			needsMetadataLookup &&
			emoji.id &&
			metadataState &&
			!metadataState.data &&
			!metadataState.loading &&
			!metadataState.error
		) {
			void ExpressionMetadata.fetchEmojiMetadata(emoji.id);
		}
	}, [needsMetadataLookup, emoji.id, metadataState]);
	if (!emoji.id) return null;
	const isAnimated = Boolean(emoji.animated);
	const sourceName = emoji.uniqueName || emoji.name;
	const cloneName = GuildEmojiCommands.sanitizeEmojiName(sourceName);
	const cloningAllowed = sourceGuild
		? sourceGuild.cloneEmojiAllowed
		: metadataState?.data
			? metadataState.data.allowCloning
			: null;
	const isLoadingPermission = needsMetadataLookup && (metadataState?.loading ?? false);
	if (cloningAllowed === false) return null;
	const eligible = Guilds.getGuilds()
		.filter((guild) => guild.id !== emoji.guildId)
		.filter((guild) => Permission.can(Permissions.MANAGE_EXPRESSIONS, {guildId: guild.id}))
		.map((guild) => {
			const max = isAnimated ? guild.maxAnimatedEmojis : guild.maxStaticEmojis;
			const used = Emoji.getGuildEmoji(guild.id).filter((e) => e.animated === isAnimated).length;
			return {guild, slotsFull: used >= max};
		})
		.sort((a, b) => a.guild.name.localeCompare(b.guild.name));
	if (eligible.length === 0) return null;
	const cloneInto = async (guildId: string) => {
		const targetGuild = Guilds.getGuild(guildId);
		const targetName = targetGuild?.name ?? '';
		const inFlightKey = `${emoji.id}:${guildId}`;
		if (!emojiCloneGuard.begin(inFlightKey)) return;
		try {
			const sourceUrl = AvatarUtils.getEmojiURL({id: emoji.id!, animated: isAnimated});
			const response = await fetch(sourceUrl);
			if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
			const blob = await response.blob();
			const image = await blobToDataUrl(blob);
			const result = await GuildEmojiCommands.bulkUpload(guildId, [{name: cloneName, image}]);
			if (result.failed.length > 0) {
				const reason = result.failed[0]?.error ?? '';
				ToastCommands.error(t`Failed to clone :${sourceName}: into ${targetName}: ${reason}`);
				return;
			}
			ToastCommands.success(t`Cloned :${sourceName}: into ${targetName}`);
		} catch (error) {
			logger.error(`Failed to clone emoji ${emoji.id} into ${guildId}`, error);
			const apiErrors = failureValidationErrors(error);
			const message = apiErrors?.map((e) => e.message).join(', ') ?? (error instanceof Error ? error.message : null);
			ToastCommands.error(message ?? t`Failed to clone emoji into ${targetName}`);
		} finally {
			emojiCloneGuard.scheduleRelease(inFlightKey);
		}
	};
	if (isLoadingPermission) {
		return (
			<MenuGroup>
				<MenuItem icon={<CopyIcon />} disabled hint={t`Checking permissions...`}>
					{t`Clone Emoji into...`}
				</MenuItem>
			</MenuGroup>
		);
	}
	return (
		<MenuGroup>
			<MenuItemSubmenu
				label={t`Clone Emoji into...`}
				render={() => (
					<MenuGroup>
						{eligible.map(({guild, slotsFull}) => (
							<MenuItem
								key={guild.id}
								icon={<GuildIcon id={guild.id} name={guild.name} icon={guild.icon} sizePx={16} />}
								disabled={slotsFull}
								hint={slotsFull ? (isAnimated ? t`Animated emoji slots full` : t`Emoji slots full`) : undefined}
								onClick={() => {
									void cloneInto(guild.id);
									onClose();
								}}
							>
								{guild.name}
							</MenuItem>
						))}
					</MenuGroup>
				)}
			/>
		</MenuGroup>
	);
});
